import { UseQueryOptions, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import type { TableView } from '@components/table';
import { toQueryString } from '@utils/requests';

import { ILoginResponse } from 'types/authentication';
import { ITODO } from 'types/todo';
import { UserDepartment } from 'types/users';

import { request, unhandledRequest } from './request';

export function loadUser(userId: number) {
  return request.get({
    url: `/users/${userId}/`,
  });
}

export function load() {
  return request.get({
    url: '/search/users/' + toQueryString({ limit: 5000 }),
  });
}

export function loadPictures() {
  return request.get({
    url: '/users/' + toQueryString({ pictures: 1 }),
  });
}

export async function loadDepartments(userId: number) {
  const response = await unhandledRequest.get<UserDepartment[]>({
    url: `/users/${userId}/departments/`,
  });
  return response.data;
}

export function twoFactor() {
  return request.get({
    url: '/two-factor/',
  });
}

export function setTwoFactor(data: ITODO) {
  return request.patch({
    url: '/two-factor/',
    data,
  });
}

export function addAuthenticator(data: ITODO) {
  return request.post({
    url: `/two-factor/authenticator/`,
    data,
  });
}

export function removeAuthenticator(data: ITODO) {
  return request.post({
    url: '/two-factor/authenticator/delete/',
    data,
  });
}

export function addSMSNumber(data: ITODO) {
  return request.post({
    url: '/two-factor/sms/add-number/',
    data,
  });
}

export function addSMSAuthenticator(data: ITODO) {
  return request.post({
    url: '/two-factor/sms/',
    data,
  });
}

export function removeSMSAuthenticator(data: ITODO) {
  return request.post({
    url: '/two-factor/sms/delete/',
    data,
  });
}

export function signIn(email: string, password: string) {
  return request.post<ILoginResponse>({
    url: '/auth/login/',
    data: { email, password },
  });
}

export function signOut() {
  return request.post<void>({
    url: '/auth/logout/',
  });
}

export function signIn2fa(uuid: string, code: string) {
  return request.post({
    url: '/auth/2fa/',
    data: { uuid, code },
  });
}

export function signInSMS(uuid: string, code: string) {
  return request.post({
    url: '/auth/sms/',
    data: { uuid, code },
  });
}

export function signInSendBackupSMS(uuid: string, code: string) {
  return request.post({
    url: '/authenticate/send_sms/',
    data: { uuid, code },
  });
}

export function signInAzureAD(token: string, email: string) {
  return request.post({
    url: '/auth/azure/',
    data: { token, email },
  });
}

type SetReturnData = {
  message: object;
};

type SetData = {
  password1: string;
  password2: string;
  passwordUuid: string;
  uuid: string;
  code?: string;
  phone?: string;
};

export type SubmitError = AxiosError<{
  [key in keyof SetData]?: string;
}>;

export async function setPassword(data: SetData) {
  const response = await unhandledRequest.patch<SetReturnData>({
    url: '/password/set/',
    data,
  });
  return response.data;
}

export type PasswordData = {
  qrCode: string;
};

export async function canSetPassword(uuid: string, passwordUuid: string) {
  const query = toQueryString({
    uuid,
    password_uuid: passwordUuid,
  });

  const response = await unhandledRequest.get<PasswordData>({
    url: '/password/set/' + query,
  });
  return response.data;
}

export function resetPassword(data: { email: string }) {
  return request.post({
    url: '/password/reset/',
    data,
  });
}

export function create(data: ITODO) {
  return request.post({
    url: '/users/',
    data,
  });
}

export function sendInvite(userId: number) {
  return request.post({
    url: `/users/${userId}/invite/`,
  });
}

export function remove(userId: number) {
  return request.delete({
    url: `/users/${userId}/`,
  });
}

export function updateDetails(userId: number, data: ITODO) {
  return request.patch({
    url: `/users/${userId}/edit/`,
    data,
  });
}

export function updateSelf(data: ITODO) {
  return request.patch({
    url: '/user/',
    data,
  });
}

export function updatePassword(data: {
  oldPassword: string;
  password1: string;
  password2: string;
}) {
  return unhandledRequest.patch({
    url: '/password/update/',
    data,
  });
}

export function authenticateSlack(code: string) {
  return request.get({
    url: '/integrations/slack/auth/' + toQueryString({ code }),
  });
}

export function loadFollowingStats() {
  return request.get({
    url: '/users-following-stats/',
  });
}

export function grantJobAccess(userId: number, jobId: number) {
  return request.post({
    url: `/users/${userId}/jobs/${jobId}/`,
  });
}

type UserApp = {
  clientId: string;
  clientName: string;
};

type UserAppResponse = {
  clientId: string;
  clientSecret: string;
};

export async function appList() {
  const response = await unhandledRequest.get<UserApp[]>({
    url: '/external/user-app/',
  });
  return response.data;
}

export async function createApp(clientName: string) {
  const response = await unhandledRequest.post<UserAppResponse>({
    url: '/external/user-app/',
    data: { clientName },
  });
  return response.data;
}

export function removeApp(clientId: string) {
  return unhandledRequest.delete({ url: `/external/user-app/${clientId}/` });
}

export type ApplicantsListSettingsResponse = {
  settings: TableView;
};

export async function getApplicantsListSettings() {
  const response = await unhandledRequest.get<ApplicantsListSettingsResponse>({
    url: '/user/applicants-list-settings/',
  });
  return response.data.settings;
}

export async function updateApplicantsListSettings(tableView: TableView) {
  await unhandledRequest.patch<void>({
    url: '/user/applicants-list-settings/',
    data: { settings: tableView },
  });
}

export const useApplicantsListSettings = () => {
  return useQuery({
    queryKey: ['applicants-list-settings'],
    queryFn: getApplicantsListSettings,
    select: (settings): TableView => {
      if (Object.keys(settings).length > 0) {
        return settings;
      }
      return {
        appliedFilters: [],
        conjunction: 'and',
        sortingState: [
          {
            id: 'date',
            desc: true,
          },
        ],
      };
    },
  });
};

export type MessagesListSettingsResponse = {
  settings: TableView;
};

export async function getMessagesListSettings() {
  const response = await unhandledRequest.get<MessagesListSettingsResponse>({
    url: '/user/messages-list-settings/',
  });

  return response.data.settings;
}

export async function updateMessagesListSettings(tableView: TableView) {
  await unhandledRequest.patch({
    url: '/user/messages-list-settings/',
    data: { settings: tableView },
  });
}

export const useMessagesListSettings = (options?: UseQueryOptions<TableView>) => {
  return useQuery({
    queryKey: ['messages-list-settings'],
    queryFn: getMessagesListSettings,
    select: (settings): TableView => {
      if (Object.keys(settings).length > 0) {
        return settings;
      }
      return {
        appliedFilters: [],
        conjunction: 'and',
        sortingState: [
          {
            id: 'created',
            desc: true,
          },
        ],
      };
    },
    ...options,
  });
};
